import Link from 'next/link'
import classNames from 'classnames'
import { IconMoreHorizontal16 } from '@hashicorp/flight-icons/svg-react/more-horizontal-16'
import * as MenuPrimitive from '../menu-primitive'
import { useMenuPrimitive } from '../menu-primitive/use-menu-primitive'
import type { HTMLAttributes, LiHTMLAttributes, ReactNode } from 'react'

interface BreadcrumbProps extends HTMLAttributes<HTMLElement> {
	/**
	 * This controls if the Breadcrumb Items can wrap if they don’t fit within the container.
	 * @default true
	 */
	itemsCanWrap?: boolean
	/**
	 * Accepts a localized string.
	 * @default 'breadcrumbs'
	 */
	ariaLabel?: string
	/**
	 * Array of Breadcrumb.Item and/or Breadcrumb.Truncation components.
	 */
	children: ReactNode
}

function Root({
	itemsCanWrap = true,
	ariaLabel = 'breadcrumbs',
	children,
	...rest
}: BreadcrumbProps) {
	return (
		<nav
			className={classNames('hds-breadcrumb', {
				['hds-breadcrumb--items-can-wrap']: itemsCanWrap,
			})}
			aria-label={ariaLabel}
			{...rest}
		>
			<ol className="hds-breadcrumb__list">{children}</ol>
		</nav>
	)
}

interface BreadcrumbItemProps extends LiHTMLAttributes<HTMLLIElement> {
	/**
	 * The URL to link to.
	 */
	href: string
	/**
	 * The text displayed within the item.
	 */
	text: string
	/**
	 * Use to show an flight icon.
	 */
	icon?: ReactNode
	/**
	 * Determines if an item is the last item in the Breadcrumb, in which case it doesn’t generate a link.
	 * @default false
	 */
	current?: boolean
	/**
	 * A parameter that can be applied to an "item" to limit its max-width
	 */
	maxWidth?: `${number}px` | `${number}em`
}

function Item({
	maxWidth,
	current,
	icon,
	text,
	href,
	...rest
}: BreadcrumbItemProps) {
	return (
		<li
			className="hds-breadcrumb__item"
			style={maxWidth ? { maxWidth } : {}}
			{...rest}
		>
			{current ? (
				<div className="hds-breadcrumb__current">
					<ItemContent icon={icon} text={text} />
				</div>
			) : (
				<Link className="hds-breadcrumb__link" href={href}>
					<ItemContent icon={icon} text={text} />
				</Link>
			)}
		</li>
	)
}

function ItemContent({
	icon,
	text,
}: Pick<BreadcrumbItemProps, 'icon' | 'text'>) {
	return (
		<>
			{icon && <div className="hds-breadcrumb__icon">{icon}</div>}

			<span className="hds-breadcrumb__text">{text}</span>
		</>
	)
}

interface BreadcrumbTruncationProps extends HTMLAttributes<HTMLLIElement> {
	/**
	 * Set on the truncation toggle button. Accepts a localized string.
	 * @default 'show more'
	 */
	ariaLabel?: string
	/**
	 * Array of Breadcrumb.Item components.
	 */
	children: ReactNode
}

function Truncation({
	ariaLabel,
	children,
	...rest
}: BreadcrumbTruncationProps) {
	return (
		<li
			className="hds-breadcrumb__item hds-breadcrumb__item--is-truncation"
			{...rest}
		>
			<MenuPrimitive.Provider>
				<MenuPrimitive.Toggle>
					<TruncationButton />
				</MenuPrimitive.Toggle>
				<MenuPrimitive.Content>
					<div className="hds-breadcrumb__truncation-content">
						<ol className="hds-breadcrumb__sublist">{children}</ol>
					</div>
				</MenuPrimitive.Content>
			</MenuPrimitive.Provider>
		</li>
	)
}

function TruncationButton({ ariaLabel = 'show more' }: { ariaLabel?: string }) {
	const { isOpen, onClickToggle } = useMenuPrimitive()

	return (
		<button
			type="button"
			className="hds-breadcrumb__truncation-toggle"
			aria-label={ariaLabel}
			aria-expanded={isOpen}
			onClick={onClickToggle}
		>
			<IconMoreHorizontal16 />
		</button>
	)
}

export { Root, Item, Truncation }
